import { Container, Flex, Text, Heading, Grid, Box } from '@radix-ui/themes';
import { useVariantValue } from '@trashlab/sitesapp/src/organizations/fasttrac/lib/utils/get-variant-value';
import { Image } from '@trashlab/sitesapp/src/organizations/fasttrac/components/shared/image';
import {
  IconRecycle,
  IconStar,
  IconCurrencyDollar,
  IconHeart,
  IconClock,
} from '@tabler/icons-react';

type ReasonItem = {
  icon: React.ReactNode;
  title: string;
  description: string;
  titleVariable: string;
  descriptionVariable: string;
};

export const BestDealReasons = () => {
  const sectionTitle = useVariantValue(
    'BEST_DEAL_REASONS_TITLE',
    '5 Great Reasons To Choose Best Deal Dumpsters',
  );

  const reasonTitles = [
    useVariantValue('BEST_DEAL_REASON_1_TITLE', 'ECO FRIENDLY'),
    useVariantValue('BEST_DEAL_REASON_2_TITLE', 'UNPARALLELED SERVICE'),
    useVariantValue('BEST_DEAL_REASON_3_TITLE', 'COMPETITIVE PRICES'),
    useVariantValue('BEST_DEAL_REASON_4_TITLE', 'HONEST & LOCAL'),
    useVariantValue('BEST_DEAL_REASON_5_TITLE', 'ALWAYS ON TIME'),
  ];

  const reasonDescriptions = [
    useVariantValue(
      'BEST_DEAL_REASON_1_DESC',
      "Need to recycle? You're in luck! Best Deal Dumpster strives to recycle as much as possible.",
    ),
    useVariantValue(
      'BEST_DEAL_REASON_2_DESC',
      'Unlike many companies in this business, we do what we say! You can count on us for on-time and on-budget deliveries every time.',
    ),
    useVariantValue(
      'BEST_DEAL_REASON_3_DESC',
      'Best Deal not only has unparalleled service and quality, but we also have the best prices.',
    ),
    useVariantValue(
      'BEST_DEAL_REASON_4_DESC',
      'We are a small, locally owned company and our customers and our community are important to us.',
    ),
    useVariantValue(
      'BEST_DEAL_REASON_5_DESC',
      "You can count on us for on-time deliveries from Best Deal Dumpsters. Don't take our word for it- check out our reviews on Google, Facebook, Angie's List and BBB.",
    ),
  ];

  const reasonsImage = useVariantValue(
    'BEST_DEAL_REASONS_IMAGE',
    '/best-deal/images/5-reasons.webp',
  );

  const icons = [
    <IconRecycle key="recycle" size={28} />,
    <IconStar key="star" size={28} />,
    <IconCurrencyDollar key="dollar" size={28} />,
    <IconHeart key="heart" size={28} />,
    <IconClock key="clock" size={28} />,
  ];

  const reasons: ReasonItem[] = reasonTitles.map((title, index) => ({
    icon: icons[index],
    title,
    description: reasonDescriptions[index],
    titleVariable: `BEST_DEAL_REASON_${index + 1}_TITLE`,
    descriptionVariable: `BEST_DEAL_REASON_${index + 1}_DESC`,
  }));

  return (
    <Container size="4" p="8">
      <Grid columns={{ initial: '1', md: '2' }} gap="8" align="center">
        {/* Left side: Image */}
        <Box
          className="flex justify-center items-center"
          data-variable="BEST_DEAL_REASONS_IMAGE"
        >
          <Image
            src={reasonsImage}
            alt="Best Deal Dumpster truck"
            className="rounded-lg shadow-lg max-h-[500px] object-cover"
          />
        </Box>

        {/* Right side: Reasons */}
        <Flex direction="column" gap="6">
          <Heading
            as="h2"
            size="7"
            weight="bold"
            className="text-[var(--primary-color)]"
            data-variable="BEST_DEAL_REASONS_TITLE"
          >
            {sectionTitle}
          </Heading>

          <Flex direction="column" gap="4">
            {reasons.map((reason, index) => (
              <Flex key={index} gap="4" align="start">
                <Box className="flex-shrink-0 w-12 h-12 rounded-full bg-[var(--primary-color)] flex items-center justify-center text-white">
                  {reason.icon}
                </Box>
                <Flex direction="column" gap="1">
                  <Text
                    size="3"
                    weight="bold"
                    className="text-[var(--primary-color)]"
                    data-variable={reason.titleVariable}
                  >
                    {reason.title}
                  </Text>
                  <Text
                    size="2"
                    className="text-neutral-600"
                    data-variable={reason.descriptionVariable}
                  >
                    {reason.description}
                  </Text>
                </Flex>
              </Flex>
            ))}
          </Flex>
        </Flex>
      </Grid>
    </Container>
  );
};

export default BestDealReasons;
